import { AlertTriangle, Check, Copy, Info, Lock, ShieldAlert, WifiOff } from 'lucide-react';
import { useState, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Button } from './Button.js';

/* -------------------------------------------------------------------------- */
/* Squelettes                                                                  */
/* -------------------------------------------------------------------------- */

export interface SkeletonProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'block' | 'line' | 'title' | 'pill' | 'avatar';
  width?: string | number;
  height?: string | number;
}

export function Skeleton({
  className,
  variant = 'block',
  width,
  height,
  style,
  ...props
}: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'skeleton',
        variant === 'line' && 'skeleton--line',
        variant === 'title' && 'skeleton--title',
        variant === 'pill' && 'skeleton--pill',
        variant === 'avatar' && 'skeleton--avatar',
        className,
      )}
      style={{ display: 'block', width, height, ...style }}
      {...props}
    />
  );
}

/** Squelette de tableau fidèle à la mise en page finale. */
export function TableSkeleton({
  rows = 5,
  columns = 5,
  label,
}: {
  rows?: number;
  columns?: number;
  label: string;
}) {
  return (
    <div className="table-wrap" role="status" aria-busy="true" aria-label={label}>
      <table className="table">
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columns }).map((__, columnIndex) => (
                <td key={columnIndex}>
                  <Skeleton variant="line" width={columnIndex === 0 ? '70%' : '45%'} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* État vide                                                                   */
/* -------------------------------------------------------------------------- */

export interface EmptyStateProps {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('empty', className)}>
      {icon ? (
        <div className="empty__ico" aria-hidden="true">
          {icon}
        </div>
      ) : null}
      <h3>{title}</h3>
      {description ? <p>{description}</p> : null}
      {action}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Bandeaux                                                                    */
/* -------------------------------------------------------------------------- */

export interface NoticeProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  tone?: 'info' | 'warning' | 'danger' | 'success' | 'violet';
  icon?: ReactNode;
  title?: ReactNode;
  actions?: ReactNode;
}

export function Notice({
  className,
  tone = 'info',
  icon,
  title,
  actions,
  children,
  ...props
}: NoticeProps) {
  const fallbackIcon =
    tone === 'danger' || tone === 'warning' ? (
      <AlertTriangle className="ico ico-20 notice__ico" aria-hidden="true" />
    ) : tone === 'success' ? (
      <Check className="ico ico-20 notice__ico" aria-hidden="true" />
    ) : (
      <Info className="ico ico-20 notice__ico" aria-hidden="true" />
    );
  return (
    <div className={cn('notice', `notice--${tone}`, className)} {...props}>
      {icon ?? fallbackIcon}
      <div className="notice__body">
        {title ? <div className="notice__title">{title}</div> : null}
        {children}
        {actions ? <div className="notice__actions">{actions}</div> : null}
      </div>
    </div>
  );
}

/** Bandeau hors ligne sticky : les actions d'écriture sont désactivées. */
export function OfflineBanner({ children }: { children: ReactNode }) {
  return (
    <div className="offline-banner" role="status">
      <WifiOff className="ico ico-16" aria-hidden="true" />
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Erreur, accès refusé, à venir                                               */
/* -------------------------------------------------------------------------- */

export function TraceId({
  traceId,
  copyLabel,
  copiedLabel,
}: {
  traceId: string;
  copyLabel: string;
  copiedLabel: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="trace-id"
      title={copyLabel}
      onClick={() => {
        void navigator.clipboard?.writeText(traceId);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      }}
    >
      {copied ? (
        <Check className="ico ico-14" aria-hidden="true" />
      ) : (
        <Copy className="ico ico-14" aria-hidden="true" />
      )}
      {copied ? copiedLabel : traceId}
    </button>
  );
}

export interface ErrorStateProps {
  title: ReactNode;
  description?: ReactNode;
  traceId?: string | undefined;
  traceCopyLabel?: string;
  traceCopiedLabel?: string;
  retryLabel?: string;
  onRetry?: () => void;
}

export function ErrorState({
  title,
  description,
  traceId,
  traceCopyLabel = 'Copier',
  traceCopiedLabel = 'Copié',
  retryLabel,
  onRetry,
}: ErrorStateProps) {
  return (
    <div className="sys-page" role="alert">
      <div className="sys-page__ico" style={{ color: 'var(--danger)' }} aria-hidden="true">
        <AlertTriangle className="ico ico-32" />
      </div>
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
      <div className="row" style={{ justifyContent: 'center', flexWrap: 'wrap' }}>
        {onRetry && retryLabel ? (
          <Button variant="primary" onClick={onRetry}>
            {retryLabel}
          </Button>
        ) : null}
        {traceId ? (
          <TraceId traceId={traceId} copyLabel={traceCopyLabel} copiedLabel={traceCopiedLabel} />
        ) : null}
      </div>
    </div>
  );
}

export function NoPermissionState({
  title,
  description,
  action,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="sys-page">
      <div className="sys-page__ico" aria-hidden="true">
        <ShieldAlert className="ico ico-32" />
      </div>
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
      {action}
    </div>
  );
}

export function ComingSoonState({
  title,
  description,
  action,
  icon,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="sys-page">
      <div className="sys-page__ico" aria-hidden="true">
        {icon ?? <Lock className="ico ico-32" />}
      </div>
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
      {action}
    </div>
  );
}
